/**
 * HTTP Client - Centralized fetch wrapper
 *
 * All browser-originated calls go through the same-origin BFF proxy
 * (`/api/proxy/*`), which attaches the access token from the HttpOnly
 * session cookie server-side. JS never sees the token.
 */

import { apiConfig } from '@/lib/config'

// ============================================================================
// TYPES
// ============================================================================

export interface FetchOptions extends Omit<RequestInit, 'body'> {
  /** Override base URL for this request (rarely needed). */
  baseURL?: string
  /** Query parameters to append to URL */
  params?: Record<string, string | number | boolean | undefined>
  /**
   * Legacy flag, retained for source compatibility. The proxy attaches auth
   * automatically from cookies, so this is a no-op.
   */
  skipAuth?: boolean
  /** Request body - will be JSON stringified for non-FormData */
  body?: RequestInit['body'] | Record<string, unknown>
}

export interface ApiError {
  message: string
  code?: string
  status?: number
  details?: Record<string, string[]>
}

export class HttpError extends Error {
  public readonly status: number
  public readonly code?: string
  public readonly details?: Record<string, string[]>

  constructor(message: string, status: number, code?: string, details?: Record<string, string[]>) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.code = code
    this.details = details
  }
}

// ============================================================================
// HTTP CLIENT
// ============================================================================

/**
 * Default base path. In the browser we call the same-origin proxy, which
 * forwards to ${BACKEND_URL}${API_PREFIX}. In a Server Component / Route
 * Handler we go straight to the backend (no proxy hop needed there).
 */
const PROXY_PREFIX = '/api/proxy'

function defaultBase(): string {
  if (typeof window === 'undefined') {
    return apiConfig.apiUrl
  }
  return PROXY_PREFIX
}

class HttpClient {
  private static instance: HttpClient

  public static getInstance(): HttpClient {
    if (!HttpClient.instance) {
      HttpClient.instance = new HttpClient()
    }
    return HttpClient.instance
  }

  // --------------------------------------------------------------------------
  // URL Construction
  // --------------------------------------------------------------------------

  private constructURL(endpoint: string, options: FetchOptions): string {
    const base = options.baseURL || defaultBase()
    // On the browser the base is a relative path (`/api/proxy`), so build via
    // window.location to keep URL happy.
    const absoluteBase = base.startsWith('http')
      ? base
      : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost') + base

    const url = new URL(`${absoluteBase}${endpoint}`)

    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value))
        }
      })
    }

    // Strip the synthetic origin for browser-side relative bases.
    if (!base.startsWith('http') && typeof window !== 'undefined') {
      return url.pathname + url.search
    }
    return url.toString()
  }

  // --------------------------------------------------------------------------
  // Core Fetch Method
  // --------------------------------------------------------------------------

  public async request<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const url = this.constructURL(endpoint, options)
    const { baseURL: _baseURL, params: _params, skipAuth: _skipAuth, body, headers, ...fetchOptions } = options

    // Prepare headers
    const requestHeaders: HeadersInit = { ...headers }

    // Prepare body
    let requestBody: RequestInit['body'] | undefined

    if (body !== undefined) {
      if (body instanceof FormData || body instanceof URLSearchParams || typeof body === 'string') {
        requestBody = body
      } else {
        requestBody = JSON.stringify(body)
        if (!(requestHeaders as Record<string, string>)['Content-Type']) {
          ;(requestHeaders as Record<string, string>)['Content-Type'] = 'application/json'
        }
      }
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers: requestHeaders,
      body: requestBody,
      credentials: 'include',
    })

    // A 401 from the proxy means either (a) the user was never authenticated
    // and hit a protected endpoint, or (b) their session expired and silent
    // refresh failed. Don't blanket-redirect from here — let the AuthContext
    // (which polls /api/auth/me) detect the session loss, and let individual
    // pages decide how to handle it (e.g. account layouts already gate on
    // isAuthenticated). Just surface as HttpError below.

    // Parse response
    let data: T | null = null
    const contentType = response.headers.get('content-type')

    if (contentType?.includes('application/json')) {
      try {
        data = await response.json()
      } catch {
        data = null as T
      }
    }

    // Handle errors
    if (!response.ok) {
      const errorData = data as { message?: string; error?: string | { message?: string; code?: string; details?: Record<string, string[]> }; code?: string; details?: Record<string, string[]> } | null
      const nested = typeof errorData?.error === 'object' ? errorData.error : null
      const message = nested?.message || errorData?.message || (typeof errorData?.error === 'string' ? errorData.error : null) || `Request failed with status ${response.status}`
      const code = nested?.code || errorData?.code || `HTTP_${response.status}`
      const details = nested?.details || errorData?.details

      throw new HttpError(message, response.status, code, details)
    }

    return data as T
  }

  // --------------------------------------------------------------------------
  // Convenience Methods
  // --------------------------------------------------------------------------

  public async get<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  public async post<T>(endpoint: string, data?: Record<string, unknown>, options: FetchOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body: data })
  }

  public async postFormData<T>(endpoint: string, formData: FormData, options: FetchOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body: formData })
  }

  public async put<T>(endpoint: string, data?: Record<string, unknown>, options: FetchOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body: data })
  }

  public async patch<T>(endpoint: string, data?: Record<string, unknown>, options: FetchOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body: data })
  }

  public async delete<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const httpClient = HttpClient.getInstance()

export type { HttpClient }
